import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/utilities/cn';
import { useAuth } from '@/shared/auth';
import { NAV_ITEMS } from './navConfig';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  className?: string;
}


export function Sidebar({ collapsed, onToggleCollapse, className }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasPermission } = useAuth();

  const filteredNavItems = NAV_ITEMS.filter(
    (item) => !item.permission || hasPermission(item.permission)
  );

  return (
    <aside
      className={cn(
        'fixed inset-y-0 start-0 z-30 flex flex-col border-e border-slate-200 bg-white transition-all duration-200',
        'dark:border-slate-800 dark:bg-slate-900',
        collapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-slate-200 px-4 dark:border-slate-800">
        <Link to="/app" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-white">
            <span className="text-lg font-bold">M</span>
          </div>
          {!collapsed && (
            <span className="text-lg font-bold text-slate-900 dark:text-white">
              MIP
            </span>
          )}
        </Link>
        <button
          onClick={onToggleCollapse}
          className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {filteredNavItems.map((item) => {
            const isActive =
              location.pathname === item.href ||
              location.pathname.startsWith(`${item.href}/`);

            return (
              <li key={item.id}>
                <Link
                  to={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'relative flex items-center gap-3 rounded-[8px] px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/25 dark:text-primary-300'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
                    collapsed && 'justify-center px-2'
                  )}
                  title={collapsed ? t(item.label) : undefined}
                >
                  {/* Active accent rail */}
                  {isActive && !collapsed && (
                    <span className="absolute inset-y-1.5 start-0 w-0.5 rounded-full bg-primary-600" aria-hidden />
                  )}
                  {item.icon}
                  {!collapsed && <span>{t(item.label)}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Sidebar footer */}
      {!collapsed && (
        <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-800">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            MIP Platform
          </p>
        </div>
      )}
    </aside>
  );
}
